import { useState } from "react";
import { supabase } from "@/integrations/supabase/client";
import { useAuth } from "@/hooks/useAuth";
import type { Workspace } from "@/hooks/useWorkspace";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Download } from "lucide-react";
import { getStripeEnvironment } from "@/lib/stripe";
import {
  buildSettingsExportFileName,
  loadWorkspaceExportData,
  triggerJsonDownload,
} from "@/lib/settingsDataExport";
import { toast } from "sonner";

type Props = { workspace: Workspace };

/**
 * Per-workspace JSON export. Lives above the workspace's danger zone so
 * users can capture their data before archiving or deleting.
 */
export const WorkspaceExportCard = ({ workspace }: Props) => {
  const { user } = useAuth();
  const [exporting, setExporting] = useState(false);

  const run = async () => {
    if (!user) return;
    setExporting(true);
    try {
      const payload = await loadWorkspaceExportData(supabase, {
        workspaceId: workspace.id,
        userId: user.id,
        environment: getStripeEnvironment(),
        userEmail: user.email,
        workspaceLabel: workspace.workspace_label,
        displayName: workspace.display_name,
        personaId: workspace.is_primary ? workspace.id : workspace.parent_profile_id,
      });
      triggerJsonDownload(buildSettingsExportFileName(workspace.workspace_label), payload);
      toast.success("Workspace export downloaded");
    } catch (e) {
      toast.error((e as Error).message);
    } finally {
      setExporting(false);
    }
  };

  return (
    <Card className="shadow-soft">
      <CardHeader>
        <CardTitle className="flex items-center gap-2">
          <Download className="h-5 w-5 text-primary" /> Export this workspace
        </CardTitle>
      </CardHeader>
      <CardContent className="space-y-3 text-sm">
        <p className="text-muted-foreground">
          Download a JSON snapshot of this workspace's data: excerpts, library items, tags, categories,
          brand colors, gradient presets, Readwise highlights, and Composer records. Encrypted fields
          (e.g. publishing connector secrets, encrypted draft bodies) are replaced with placeholders.
        </p>
        <Button variant="outline" onClick={run} disabled={exporting}>
          {exporting ? "Preparing JSON export…" : "Download JSON export"}
        </Button>
      </CardContent>
    </Card>
  );
};
